import { useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import type { EventInvite, Profile } from "@/api/bale.zod";

import {
  useDeleteEventInvite,
  useGetProfileSuspense,
  useListEventInvitesSuspense,
  useListFriendsSuspense,
  useUpdateEventInvite,
} from "@/api/bale";
import { ActionBox } from "@/components/ActionBox";
import { Button } from "@/components/Button";
import { DegreeOfSeparationInput } from "@/components/DegreeOfSeparationInput";
import { Dropdown } from "@/components/Dropdown";
import { Icon } from "@/components/Icon";
import { IconButton } from "@/components/IconButton";
import { Input } from "@/components/Input";
import { MenuField } from "@/components/MenuField";
import { MenuItem } from "@/components/MenuItem";
import { Missing } from "@/components/Missing";
import { ProfileItem } from "@/components/ProfileItem";

import { useCurrentEventSuspense } from "../../../../hooks/useCurrentEvent";

export function InvitesSection() {
  const { t } = useTranslation();
  const id = useId();
  const popover = useRef<HTMLDivElement>(null);
  const [search, setSearch] = useState("");
  const event = useCurrentEventSuspense({ query: { select: (data) => data.data.event } });
  const me = useGetProfileSuspense("@me", { query: { select: (data) => data.data.profile } });
  const invites = useListEventInvitesSuspense(event.data.id, {
    query: {
      select: (data) => data.data.event_invites.filter((invite) => invite.inviter_account_id === me.data.id),
    },
  });
  const alreadyInvited = new Set(invites.data.map((invite) => invite.invited.id));
  const friends = useListFriendsSuspense("@me", { query: { select: (data) => data.data.friends } });
  const uninvitedFriends = friends.data
    .filter((friend) => !alreadyInvited.has(friend.profile.id))
    .filter(
      (friend) =>
        friend.profile.id.toLowerCase().startsWith(search.toLowerCase()) ||
        friend.profile.display_name?.toLowerCase().startsWith(search.toLowerCase()) ||
        friend.profile.real_name?.toLowerCase().startsWith(search.toLowerCase()) ||
        friend.note?.toLowerCase().startsWith(search.toLowerCase()),
    );

  const updateInvite = useUpdateEventInvite();

  function inviteFriend(friend: Profile) {
    updateInvite.mutate({ eventId: event.data.id, accountId: friend.id, data: { discoverable_distance: 0 } });
    popover.current?.hidePopover();
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-4 px-4">
        <h3 className="font-interest">{t("invite-friends")}</h3>
        <p className="font-disinterest">{t("invite-friends-tutorial")}</p>
        {invites.data.length === 0 && <Missing>{t("event-not-inviting")}</Missing>}
      </div>
      <div className="flex flex-col border-divider not-empty:border-b">
        {invites.data.map((invite) => (
          <EventInviteItem key={invite.invited.id} invite={invite} />
        ))}
      </div>
      <div className="px-4">
        <Button type="button" popoverTarget={id} className="flex flex-row items-center justify-between gap-2">
          <Icon name="plus" />
          {t("invite-friend-action")}
        </Button>
      </div>
      <Dropdown
        id={id}
        popover="auto"
        className="absolute -ml-2 w-max min-w-60 [position-area:right_span-bottom]"
        ref={popover}
        onToggle={() => setSearch("")}
      >
        <MenuField>
          <Icon name="search" />
          <Input
            type="search"
            placeholder={t("search")}
            value={search}
            onChange={(event) => setSearch(event.currentTarget.value)}
            autoFocus
          />
        </MenuField>
        {uninvitedFriends.map((friend) => (
          <MenuItem key={friend.profile.id} onClick={() => inviteFriend(friend.profile)}>
            <ProfileItem profile={friend.profile} note={friend.note} noLink />
          </MenuItem>
        ))}
        {uninvitedFriends.length === 0 && <MenuItem disabled>{t("invite-no-friends-left")}</MenuItem>}
      </Dropdown>
    </div>
  );
}

function EventInviteItem({ invite }: { invite: EventInvite }) {
  const updateInvite = useUpdateEventInvite();
  const deleteInvite = useDeleteEventInvite();
  const friends = useListFriendsSuspense("@me", { query: { select: (data) => data.data.friends } });
  const event = useCurrentEventSuspense({ query: { select: (data) => data.data } });
  const me = useGetProfileSuspense("@me", { query: { select: (data) => data.data.profile } });
  const note = friends.data.find((friend) => friend.profile.id === invite.invited.id)?.note;
  const participantMe = event.data.event_participants.find((participant) => participant.profile.id === me.data.id);
  return (
    <div className="flex flex-row items-center justify-between border-t border-divider px-4 py-2">
      <ProfileItem profile={invite.invited} note={note} />
      <ActionBox>
        <DegreeOfSeparationInput
          naming="invitability"
          defaultValue={invite.discoverable_distance}
          max={participantMe?.limiting_discoverable_distance}
          onChange={(distance) =>
            updateInvite.mutate({
              eventId: event.data.event.id,
              accountId: invite.invited.id,
              data: { discoverable_distance: distance },
            })
          }
        />
        <IconButton
          type="button"
          name="trash"
          onClick={() => deleteInvite.mutate({ eventId: event.data.event.id, accountId: invite.invited.id })}
          className="text-danger"
        />
      </ActionBox>
    </div>
  );
}
